import {Checkbox} from '@/base/ui/checkbox'
import {Label} from '@/base/ui/label'
import {cn, randomId} from '@/lib/utils'
import {cva} from 'class-variance-authority'
import React from 'react'

export type IWuCheckboxProps = Omit<
  React.ComponentPropsWithRef<typeof Checkbox>,
  'onChange'
> & {
  disabled?: boolean
  checked?: boolean
  label?: string
  labelPosition?: 'left' | 'right'
  partial?: boolean
  readonly?: boolean
  required?: boolean
  onChange?: (e: boolean) => void
}
/**
 * This extends to HTML button. So it supports all attributes of button.
 * Note: className will apply to container. Style will apply to checkbox.
 */
export const WuCheckbox = React.forwardRef<HTMLButtonElement, IWuCheckboxProps>(
  (props, ref) => {
    const {
      disabled,
      checked,
      label,
      labelPosition = 'right',
      className,
      id = randomId(),
      partial,
      onChange,
      required,
      readonly,
      ...restProps
    } = props

    React.useEffect(() => {
      if (restProps.defaultChecked !== undefined && checked !== undefined) {
        console.error(
          'WuCheckbox: Both `checked` and `defaultChecked` cannot be used together. Use only one of them.',
        )
      }
    }, [restProps.defaultChecked, checked])
    return (
      <div
        className={labelPositionClasses({
          labelPosition,
          className: 'wu-checkbox-container',
        })}
        dir={restProps.dir}
      >
        <Checkbox
          id={id}
          {...restProps}
          partial={partial}
          checked={checked}
          disabled={disabled || readonly}
          onCheckedChange={onChange}
          className={cn('peer wu-checkbox', className)}
          required={required}
          ref={ref}
        />
        {label && (
          <Label htmlFor={id} className="w-full">
            {label}
          </Label>
        )}
      </div>
    )
  },
)

const labelPositionClasses = cva('flex items-center gap-2 w-fit', {
  variants: {
    labelPosition: {
      left: 'flex-row-reverse text-right',
      right: 'flex-row',
    },
  },
})

WuCheckbox.displayName = 'WuCheckbox'
